import Text from "@/components/common/Typography/Text";
import ArrowLeftRightHalfIcon from "@/components/icons/ArrowLeftRightHalfIcon";
import FightUpIcon from "@/components/icons/FightUpIcon";
import { Card, CardProps, Col, Row } from "antd";
import { FC, useState } from "react";
import { useAppDispatch } from "@/lib/redux/hooks";
import { flightActions } from "@/lib/redux/features/flight/reduce";
import { useAirports } from "@/hooks/flight/useAirports";
import { getQueryStringSearchFlight } from "@/utils/string/getQueryStringSearchFlight";
import { useTETranslation } from "@/hooks/app/useTETranslation";
import { useTranslation } from "react-i18next";
import { useAirlines } from "@/hooks/flight/useAirlines";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faPlane } from "@fortawesome/free-solid-svg-icons";

interface FlightDealItemProps extends Omit<CardProps, "variant"> {
  deal: any;
  shown?: boolean;
  styles?: any;
  variant?: "default" | "poster" | "good-price" | "international";
}

const FlightDealItem: FC<FlightDealItemProps> = ({ deal, shown, variant = "default", ...props }) => {
  const dispatch = useAppDispatch();
  const airports = useAirports()
  const { t, i18n } = useTranslation()
  const { getFormatCurrency, getValidDayjs, getAirportCityName } = useTETranslation()
  const airlines = useAirlines()

  const airportFrom = airports[deal?.productMetaData?.startPoint]
  const airportTo = airports[deal?.productMetaData?.endPoint]
  const airline = airlines[deal?.productMetaData?.airline || "VN"]
  const imageUrl = deal?.image || deal?.thumbnail || deal?.productMetaData?.banner || deal?.productMetaData?.thumbnail
  const routeTitle = deal?.title || `${getAirportCityName(airportFrom) || deal?.productMetaData?.startPoint} – ${getAirportCityName(airportTo) || deal?.productMetaData?.endPoint}`
  const [imageFailed, setImageFailed] = useState(false)

  const handleDealClick = (e: any) => {
    e.preventDefault()
    if (deal) {
      dispatch(flightActions.updateModeSearchFlight({ isMultiCities: false }))
      dispatch(flightActions.changeCheckedItinerary(false))

      dispatch(flightActions.selectFromAirport({ indexInputFlight: 0, fromAirport: airportFrom }))
      dispatch(flightActions.selectToAirport({ indexInputFlight: 0, toAirport: airportTo }))
      dispatch(flightActions.changeDateFrom({ indexInputFlight: 0, dateFrom: getValidDayjs(deal.productMetaData.startDate).format('YYYY-MM-DD') }))

      const newSearchString = getQueryStringSearchFlight()
      window.open(`/${i18n.language}/flights/result?${newSearchString}`, '_blank')
    };
  }

  if (variant === "poster") {
    const price = new Intl.NumberFormat("vi-VN").format(parseFloat(deal?.flashPrice || "0"))
    return (
      <article
        className={`${props.className ? `${props.className} ` : ""}home__deals-flights--item home__deals-flights--poster`}
        onClick={handleDealClick}
      >
        <div className="home__deals-flights--poster-media">
          {imageUrl && !imageFailed ? (
            <img src={imageUrl} alt={routeTitle} onError={() => setImageFailed(true)} />
          ) : null}
          <span className="home__deals-flights--poster-badge">
            {deal?.tripTypeLabel || t("flight.one_way")}
          </span>
        </div>
        <div className="home__deals-flights--poster-body">
          <Text className="home__deals-flights--poster-title">{routeTitle}</Text>
          <div className="home__deals-flights--poster-date">
            {airline?.url ? <img src={airline.url} alt={airline.name || "Airline"} /> : null}
            <Text type="secondary">
              {getValidDayjs(deal?.productMetaData?.startDate).format("D [thg] M YYYY")}
            </Text>
          </div>
          <Text className="home__deals-flights--poster-price">
            {price} {deal?.currency || "VND"}
          </Text>
        </div>
      </article>
    )
  }

  if (variant === "international") {
    const originalPrice = parseFloat(deal?.originalPrice || "0")
    const flashPrice = parseFloat(deal?.flashPrice || "0")
    const saving = Math.max(originalPrice - flashPrice, 0)
    const discountPercent = originalPrice > 0 ? Math.floor((saving / originalPrice) * 100) : 0
    const fromCity = getAirportCityName(airportFrom) || deal?.productMetaData?.startPoint
    const toCity = getAirportCityName(airportTo) || deal?.productMetaData?.endPoint

    return (
      <article
        className={`${props.className ? `${props.className} ` : ""}home__deals-flights--item home__deals-flights--international`}
        onClick={handleDealClick}
      >
        <div className="home__deals-flights--international-media">
          {imageUrl && !imageFailed ? (
            <img src={imageUrl} alt={`${fromCity} đến ${toCity}`} onError={() => setImageFailed(true)} />
          ) : null}
          <span className="home__deals-flights--international-trip-type">
            <FontAwesomeIcon icon={faPlane} />
            {deal?.tripTypeLabel || t("flight.one_way")}
          </span>
          {discountPercent > 0 && (
            <span className="home__deals-flights--international-discount">
              Giảm {discountPercent}%
            </span>
          )}
        </div>
        <div className="home__deals-flights--international-body">
          <div className="home__deals-flights--international-route">
            <span>{fromCity}</span>
            <span className="home__deals-flights--international-arrow">→</span>
            <span>{toCity}</span>
          </div>
          <div className="home__deals-flights--international-date">
            <FontAwesomeIcon icon={faCalendarDays} />
            <span>Thời gian đi: {getValidDayjs(deal?.productMetaData?.startDate).format("DD/MM/YYYY")}</span>
          </div>
          <div className="home__deals-flights--international-prices">
            <Text className="home__deals-flights--international-original-price">
              {getFormatCurrency(originalPrice)}
            </Text>
            <div className="home__deals-flights--international-price-row">
              <Text className="home__deals-flights--international-price">
                {getFormatCurrency(flashPrice)}
              </Text>
              {saving > 0 && (
                <span className="home__deals-flights--international-saving">
                  <span className="home__deals-flights--international-saving-copy">
                    <span>Tiết kiệm</span>
                    <strong>{getFormatCurrency(saving)}</strong>
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </article>
    )
  }

  if (variant === "good-price") {
    const originalPrice = parseFloat(deal?.originalPrice || "0")
    const flashPrice = parseFloat(deal?.flashPrice || "0")
    const saving = Math.max(originalPrice - flashPrice, 0)
    const discountPercent = originalPrice > 0 ? Math.floor((saving / originalPrice) * 100) : 0
    return (
      <article
        className={`${props.className ? `${props.className} ` : ""}home__deals-flights--item home__deals-flights--good-price`}
        onClick={handleDealClick}
      >
        <div className="home__deals-flights--good-price-media">
          {imageUrl && !imageFailed ? (
            <img
              src={imageUrl}
              alt={routeTitle}
              onError={() => setImageFailed(true)}
            />
          ) : null}
          <span className="home__deals-flights--good-price-badge">
            {deal?.tripTypeLabel || t("flight.one_way")}
          </span>
          {discountPercent > 0 && (
            <span className="home__deals-flights--good-price-discount">
              -{discountPercent}%
            </span>
          )}
          <span className="home__deals-flights--good-price-from-price">
            Giá tốt chỉ từ
          </span>
        </div>
        <div className="home__deals-flights--good-price-body">
          <Text className="home__deals-flights--good-price-title">
            {routeTitle}
          </Text>
          <div className="home__deals-flights--good-price-date">
            {airline?.url ? <img src={airline.url} alt={airline.name || "Airline"} /> : null}
            <Text type="secondary">
              {getValidDayjs(deal?.productMetaData?.startDate).format("D [thg] M YYYY")}
            </Text>
          </div>
          <div className="home__deals-flights--good-price-prices">
            <Text className="home__deals-flights--good-price-original-price">
              {getFormatCurrency(originalPrice)}
            </Text>
            <div className="home__deals-flights--good-price-price-row">
              <Text className="home__deals-flights--good-price-price">
                {getFormatCurrency(flashPrice)}
              </Text>
              {saving > 0 && (
                <span className="home__deals-flights--good-price-saving">
                  <span>Tiết kiệm</span>
                  <strong>{getFormatCurrency(saving)}</strong>
                </span>
              )}
            </div>
          </div>
        </div>
      </article>
    )
  }

  return (
    <Card
      hoverable
      size="small"
      {...props}
      className={`${props.className ? `${props.className} ` : ""}home__deals-flights--item`}
      style={{ cursor: "pointer" }}
      styles={{
        ...props.styles,
        body: {
          display: "flex",
          flexDirection: "column",
          ...props.styles?.body,
        },
      }}
      cover={deal?.productMetaData?.flightType === 'INTERNATIONAL' && <img alt={`Vé máy bay ${getAirportCityName(airportFrom)} đi ${getAirportCityName(airportTo)} giá tốt`} style={{ height: "20rem" }} src={deal?.productMetaData?.banner} />}
      onClick={handleDealClick}
    >
      <Row style={{ minHeight: deal?.productMetaData?.flightType === 'DOMESTIC' ? "14rem" : "11rem" }}>
        <Col span={24}>
          <Row>
            <Col span={24}>
              {deal?.productMetaData?.flightType === 'DOMESTIC' && (<Row>
                <img
                  src={airline?.url}
                  alt={`${airline?.name || "Airline"} logo`}
                  style={{
                    height: '3.2rem',
                    width: "auto",
                    maxWidth: '30rem',
                    maxHeight: '4rem',
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Row>)}
              <Row
                className="home__deals-flights--item-title"
                align="middle"
                wrap={false}
                gutter={[10, 10]}
              >
                <Col>
                  <Text>{getAirportCityName(airportFrom)}</Text>
                </Col>
                <Col>
                  <Text>
                    <ArrowLeftRightHalfIcon />
                  </Text>
                </Col>
                <Col>
                  <Text>{getAirportCityName(airportTo)}</Text>
                </Col>
              </Row>
              <Row className="home__deals-flights--item-date">
                <Text type="secondary">
                  <FightUpIcon /> {t("flight.departure_time")}:{" "}
                  {getValidDayjs(deal?.productMetaData?.startDate).format("DD/MM/YYYY")}
                </Text>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} style={{ height: 'fit-content', alignSelf: 'end' }}>
          <Row justify="end">
            <Text type='secondary' strong delete style={{ textAlign: 'end' }}>
              {getFormatCurrency(parseFloat(deal?.originalPrice || "0"))}
            </Text>
          </Row>
          <Row align="middle" gutter={[10, 10]} justify="end">
            <Col>
              <Text strong type="danger" style={{ textAlign: 'end', fontSize: '1.8rem' }}>
                {getFormatCurrency(parseFloat(deal?.flashPrice || "0"))}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  )
};

export default FlightDealItem
